// @ts-check

// Vendor imports
const React = require('react')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')

/**
 * @typedef {Object} ModalDepthContext
 * @property {number} depth
 * @property {() => void} decrement
 * @property {() => void} increment
 */

/** @type {React.Context<ModalDepthContext>} */
const context = React.createContext({
  depth: 0,
  decrement: () => {},
  increment: () => {}
})

// React Components

/**
 * React Component that provides the modal depth context.
 * @param {Object} props
 * @param {React.ElementType} props.children
 */
function ModalDepthManager(props) {
  const [ depth, setDepth ] = React.useState(0)
  const decrement = React.useCallback(
    () => { setDepth(depth => depth - 1) },
    [ setDepth ]
  )
  const increment = React.useCallback(
    () => { setDepth(depth => depth + 1) },
    [ setDepth ]
  )
  return (
    tag(context.Provider, {
      value: {
        depth,
        decrement,
        increment
      }
    },
      props.children
    )
  )
}

/**
 * React Component that gets the current modal depth from the context.
 * Use this in class components where `useModalDepth` cannot be used directly.
 * @param {Object} props
 * @param {(depth: number) => React.ElementType} props.children
 * @returns {React.ElementType}
 */
function ModalDepthConsumer(props) {
  const depth = useModalDepth()
  return props.children(depth)
}

/**
 * React Component that updates the modal depth when mounted/unmounted.
 * Use this in class components where `useModalDepthModifier` cannot be used directly.
 * @returns {React.ElementType}
 */
function ModalDepthModifier() {
  useModalDepthModifier()
  return null
}

/**
 * React Component that prevents scrolling on `body` when modal depth > 0
 * @returns {React.ElementType}
 */
function ModalDepthBody() {
  useModalDepthBody()
  return null
}

/**
 * @param {Object} props
 * @param {React.RefObject<HTMLElement>} props.forwardedRef
 * @returns {React.ElementType}
 */
function ModalDepthScrollbarOffset(props) {
  useModalDepthScrollbarOffset(props.forwardedRef)
  return null
}

/**
 * @param {Object} props
 * @param {React.RefObject<HTMLElement>} props.scrollableRef
 * @returns {React.ElementType}
 */
function ModalDepthDisableTouchScrollingBehindOverlay(props) {
  useModalDepthDisableTouchScrollingBehindOverlay(props.scrollableRef)
  return null
}

/**
 * @param {Object} props
 * @param {React.RefObject<HTMLElement>} props.scrollableRef
 * @returns {React.ElementType}
 */
function ModalDepthOverlay(props) {
  useModalDepthOverlay(props.scrollableRef)
  return null
}

// React Hooks

/**
 * React Hook that gets the current modal depth from the context.
 * @returns {number}
 */
function useModalDepth() {
  const { depth } = React.useContext(context)
  return depth
}

/**
 * React Hook that updates the modal depth when mounted/unmounted.
 * @returns {void}
 */
function useModalDepthModifier() {
  const { increment, decrement } = React.useContext(context)
  React.useEffect(
    () => {
      increment()
      return () => {
        decrement()
      }
    },
    [ increment, decrement ]
  )
}

/**
 * React Hook that prevents scrolling on `body` when modal depth > 0
 * @returns {void}
 */
function useModalDepthBody() {
  const isBodyScrollingDisabled = useModalDepth() > 0
  React.useEffect(
    () => {
      if (typeof document === 'undefined') {
        return
      }
      if (isBodyScrollingDisabled) {
        const el = document.body
        const scrollbarWidth = window.innerWidth - el.clientWidth
        const borderRight = `${scrollbarWidth}px solid transparent`
        el.style.setProperty('border-right', borderRight)
        el.style.setProperty('overflow', 'hidden')
        return () => {
          el.style.removeProperty('border-right')
          el.style.removeProperty('overflow')
        }
      }
    },
    [ isBodyScrollingDisabled ]
  )
}

/**
 * React Hook that sets border-right when modal depth > 0
 * to compensate for the missing scrollbar.
 * @param {React.RefObject<HTMLElement>} domRef
 * @returns {void}
 */
function useModalDepthScrollbarOffset(domRef) {
  const isBodyScrollingDisabled = useModalDepth() > 0
  React.useEffect(
    () => {
      if (typeof document === 'undefined') {
        return
      }
      const el = domRef.current
      if (isBodyScrollingDisabled && el) {
        const bodyStyle = document.body.style
        const borderRight = bodyStyle.getPropertyValue('border-right')
        el.style.setProperty('border-right', borderRight)
        return () => {
          el.style.removeProperty('border-right')
        }
      }
    },
    [ domRef, isBodyScrollingDisabled ]
  )
}

/**
 * React Hook to prevent scrolling behind the overlay on iOS
 * @see https://stackoverflow.com/questions/41594997
 * @param {React.RefObject<HTMLElement>} scrollableRef
 * @returns {void}
 */
function useModalDepthDisableTouchScrollingBehindOverlay(scrollableRef) {
  const isBodyScrollingDisabled = useModalDepth() > 0
  React.useEffect(
    () => {
      if (typeof document === 'undefined') {
        return
      }
      const el = scrollableRef.current
      if (isBodyScrollingDisabled && el) {
        /** @type {number} */
        let startClientY
        const onTouchStart = (/** @type {TouchEvent} */ event) => {
          if (event.targetTouches.length === 1) {
            startClientY = event.targetTouches[0].clientY
          }
        }
        const onTouchMove = (/** @type {TouchEvent} */ event)  => {
          if (event.targetTouches.length === 1) {
            const moveClientY = event.targetTouches[0].clientY
            const diffY = moveClientY - startClientY
            if (el.scrollTop === 0 && diffY > 0) {
              event.preventDefault()
              return
            }
            const isFullyScrolled = el.scrollHeight - el.scrollTop <= el.clientHeight
            if (isFullyScrolled && diffY < 0) {
              event.preventDefault()
            }
          }
        }
        el.addEventListener('touchstart', onTouchStart, false)
        el.addEventListener('touchmove', onTouchMove, false)
        return () => {
          el.removeEventListener('touchstart', onTouchStart, false)
          el.removeEventListener('touchmove', onTouchMove, false)
        }
      }
    },
    [ scrollableRef, isBodyScrollingDisabled ]
  )
}

/**
 * React Hook for convenience; simple overlays can use this to
 * apply ModalDepthModifier and prevent scrolling behind the overlay on iOS.
 * @param {React.RefObject<HTMLElement>} scrollableRef
 * @returns {void}
 */
function useModalDepthOverlay(scrollableRef) {
  useModalDepthModifier()
  useModalDepthDisableTouchScrollingBehindOverlay(scrollableRef)
}

// React Component Prop-Types

if (__DEV__) {
  // Prop-checking imports
  const PropTypes = require('prop-types')
  const morePropTypes = require('client-v2/common/utils/prop-types')

  ModalDepthManager.propTypes = {
    children: PropTypes.node
  }

  ModalDepthConsumer.propTypes = {
    children: PropTypes.func.isRequired
  }

  ModalDepthScrollbarOffset.propTypes = {
    forwardedRef: morePropTypes.domRef
  }

  ModalDepthDisableTouchScrollingBehindOverlay.propTypes = {
    scrollableRef: morePropTypes.domRef
  }

  ModalDepthOverlay.propTypes = {
    scrollableRef: morePropTypes.domRef
  }
}

module.exports = {
  ModalDepthManager,
  ModalDepthConsumer,
  ModalDepthModifier,
  ModalDepthBody,
  ModalDepthScrollbarOffset,
  ModalDepthDisableTouchScrollingBehindOverlay,
  ModalDepthOverlay,
  useModalDepth,
  useModalDepthModifier,
  useModalDepthBody,
  useModalDepthScrollbarOffset,
  useModalDepthDisableTouchScrollingBehindOverlay,
  useModalDepthOverlay
}
